import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import TwoFAScannerIcon from "assets/svg/TwoFAScannerIcon";
import CopyIcon from "assets/svg/CopyIcon"; // ✅ Make sure this icon exists
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { QRCode } from "react-qrcode-logo";
import { fetch2FACode } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";

interface ModalProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

const TwoFAEnabler: React.FC<ModalProps> = ({
  open,
  handleClose,
  onSuccess,
}) => {
  const [QRUrl, setQRUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const getTwoFACode = async () => {
    try {
      setLoading(true);
      const response = await fetch2FACode();
      if (response && response.status === 200) {
        setQRUrl(response.data?.data?.otpauthUrl);
        setSecret(response.data?.data?.secret); // ✅ secret for manual entry
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!secret) return;

    try {
      await navigator.clipboard.writeText(secret);
      toast.info("Secret copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy the secret");
      console.error(err);
    }
  };

  useEffect(() => {
    if (open) getTwoFACode(); // Fetch only when modal opens
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      aria-labelledby="enable-2fa-modal"
      aria-describedby="enable-2fa-description"
    >
      <Box sx={styles.modalContainer}>
        <Box sx={styles.iconContainer}>
          <TwoFAScannerIcon />
        </Box>

        <Typography sx={styles.titleText}>
          Turn on 2-factor authentication
        </Typography>

        <Typography sx={styles.descriptionText}>
          Download any authenticator app on your mobile phone
        </Typography>

        {loading ? (
          <CircularProgress sx={{ my: 4 }} />
        ) : (
          <>
            <QRCode
              size={180}
              ecLevel="Q"
              value={QRUrl || ""}
              logoPadding={10}
              logoHeight={40}
              logoWidth={40}
              qrStyle="dots"
              logoPaddingStyle="circle"
            />
            <Typography sx={{ ...styles.scanText, marginTop: "1em" }}>
              Scan this QR Code
            </Typography>

            {secret && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: 1,
                  marginBottom: "1em",
                  marginTop: "1em",
                  cursor: "pointer",
                }}
                onClick={handleCopyToClipboard}
              >
                <Typography sx={styles.scanText}>Or</Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography sx={styles.scanText}>
                    copy code and enter manually
                  </Typography>
                  <CopyIcon height={20} width={20} />
                </Box>
              </Box>
            )}
          </>
        )}

        <PrimaryButton
          title="Continue"
          onClick={() => {
            onSuccess();
          }}
        />
      </Box>
    </Modal>
  );
};

export default TwoFAEnabler;

const styles = {
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 400,
    maxWidth: { xs: window.innerWidth * 0.8, md: window.innerWidth * 0.5 },
    bgcolor: "#FFFFFF",
    borderRadius: "2em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: 4,
    py: 2,
  },
  iconContainer: {
    border: "1px solid rgba(232, 232, 232, 1)",
    borderRadius: "0.8em",
    height: 52,
    width: 52,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  titleText: {
    fontSize: responsiveFontSize(20),
    fontWeight: 400, // ✅ fixed typo
    color: theme.palette.text.primary,
    marginTop: "1em",
  },
  descriptionText: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: "1em",
    marginBottom: "1em",
    textAlign: "center",
  },
  scanText: {
    fontSize: responsiveFontSize(15),
    fontWeight: 400,
    color: theme.palette.primary.main,
  },
};
